import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, Target, Search, TrendingDown, AlertTriangle, Zap, Users, Mail, MessageSquare, Phone, PhoneCall, DollarSign } from "lucide-react";
import AnimatedCounter from "@/components/AnimatedCounter";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import ClientMarquee from "@/components/ClientMarquee";
import HomeFaq from "@/components/HomeFaq";

const growthImg = "https://static.prod-images.emergentagent.com/jobs/0626e00a-f678-49da-8aaa-94155fdeec8c/images/c3937fbb7fc8130b1c3a1632d2e429601ce0161e4b0efbc0f2fd90fc3c12ec5f.png";

const stats = [
  { value: 3.54, suffix: "M+", decimals: 2, label: "YouTube subscribers", eyebrow: "Client channels" },
  { value: 15, suffix: "+", decimals: 0, label: "Years in brand & growth", eyebrow: "Founder track record" },
  { value: 80, suffix: "%", decimals: 0, label: "Leads lost in poor follow-up", eyebrow: "The industry leak we fix" },
];

const problems = [
  { icon: TrendingDown, title: "Ad costs are climbing", desc: "Meta CPMs rise every quarter while your ROAS quietly bleeds." },
  { icon: AlertTriangle, title: "Leads arrive half-decided", desc: "70%+ of buyers make up their mind before they ever hop on your call." },
  { icon: Search, title: "Vanity metrics, no pipeline", desc: "Views and follows without a system that turns them into revenue." },
];

const offerings = [
  {
    tag: "Digital Sales Engine",
    title: "A 6-stage acquisition system.",
    desc: "Paid Traffic → Landing Page → Automated Booking → WhatsApp & Email Nurture → Value-First Call → Client Conversion. Installed end-to-end.",
    cta: "Explore Digital Sales Engine",
    link: "/services/digital-sales-engine",
    icon: Zap,
  },
  {
    tag: "Personal Branding",
    title: "The 30·30·40 Framework™.",
    desc: "Authority · Relatability · Virality. Build a founder brand on YouTube and Instagram that out-ranks and out-trusts your category.",
    cta: "Explore Personal Branding",
    link: "/services/personal-branding",
    icon: Flame,
  },
];

const engineSteps = [
  { n: "01", icon: Search, t: "Paid Traffic", d: "Meta & Search campaigns tuned for intent, not clicks." },
  { n: "02", icon: Target, t: "Landing Page", d: "One page, one offer, zero friction." },
  { n: "03", icon: PhoneCall, t: "Automated Booking", d: "Qualification questions filter out time-wasters." },
  { n: "04", icon: MessageSquare, t: "WA & Email Nurture", d: "Sequences follow up the 80% who aren't ready on day one." },
  { n: "05", icon: Users, t: "Value-First Call", d: "Consultative frame that delivers clarity before the offer." },
  { n: "06", icon: DollarSign, t: "The Invitation", d: "Sales calls become natural invitations — not pitches." },
];

export default function HomePage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">

        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-28 md:pt-24 md:pb-40">
          <Reveal>
            <div className="eyebrow mb-6">Personal Branding · AI Client Acquisition</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-display text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
              Build authority. <em className="gold-shine font-medium not-italic">Automate</em> acquisition.
              <br />
              <span className="text-zinc-300">Scale </span>
              <span className="text-red-500 red-glow-text">revenue.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed font-body">
              BrandChilly builds high-converting personal brands and AI-powered client acquisition engines for
              founders, coaches and agencies who demand <span className="text-white">predictable growth</span> —
              not another random content push.
            </p>
            <p className="mt-4 max-w-2xl text-zinc-500 text-sm md:text-base font-body" data-testid="home-seo-line">
              A top <strong className="text-white font-medium">digital marketing agency in India</strong>, based in Delhi NCR — trusted by
              founder-led brands for personal branding, performance ads and lead generation.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/services/digital-sales-engine" className="btn-primary" data-testid="hero-explore-engine-btn">
                Explore Digital Sales Engine <ArrowUpRight size={18} />
              </Link>
              <Link to="/book" className="btn-ghost" data-testid="hero-book-audit-btn">
                Book a Growth Audit
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.45}>
            <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs tracking-[0.2em] uppercase text-zinc-500">
              <span className="text-red-500">✦</span>
              <span>30+ qualified calls / month</span>
              <span className="text-zinc-700">/</span>
              <span>Under 90 days</span>
              <span className="text-zinc-700">/</span>
              <span>Or you don't pay</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MAIN PITCH */}
      <section className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="font-serif-display text-3xl md:text-5xl leading-[1.15] text-white">
              We help coaches, founders and agency owners build an{" "}
              <em className="gold-shine font-medium not-italic">unmissable personal brand</em>{" "}
              and an <span className="text-red-500">automated AI acquisition engine</span> that generates{" "}
              <span className="text-white">30+ qualified sales calls every month</span> — in under 90 days,
              without spending hours scripting content or burning budget on cold outreach.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-zinc-500 text-sm tracking-[0.2em] uppercase">Or you don't pay.</p>
          </Reveal>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="relative py-24 md:py-32 border-t border-white/5 tl-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow mb-5">The uncomfortable truth</div>
            <h2 className="font-serif-display text-4xl md:text-6xl max-w-3xl leading-[1.05]">
              High ad costs. <span className="text-red-500">Low-quality leads.</span> Unpredictable revenue.
            </h2>
            <p className="mt-7 max-w-3xl text-zinc-400 text-base md:text-lg leading-relaxed font-body">
              The old playbook — "post, hope, chase, repeat" — is broken. Meta costs rise year-over-year,
              buyers arrive 70%+ decided before they ever speak to you, and traditional agency retainers
              leave you with vanity metrics instead of actual pipeline revenue.
            </p>
          </Reveal>

          <StaggerGroup className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {problems.map((p, i) => {
              const Icon = p.icon;
              return (
                <StaggerItem key={i}>
                  <div className="luxury-card p-8 h-full" data-testid={`home-problem-${i}`}>
                    <div className="w-12 h-12 bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6" style={{ borderRadius: 0 }}>
                      <Icon size={20} className="text-red-500" />
                    </div>
                    <h3 className="font-serif-display text-2xl md:text-3xl mb-3">{p.title}</h3>
                    <p className="text-zinc-400 font-body leading-relaxed">{p.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>

          <Reveal delay={0.15}>
            <div className="mt-12 luxury-card p-8 md:p-10 border-l-4 border-red-500 max-w-4xl">
              <div className="eyebrow mb-3 text-red-400">The fix</div>
              <p className="text-white font-serif-display text-2xl md:text-3xl leading-snug">
                You don't need another agency taking random stabs at marketing. You need a structured
                <span className="text-red-500"> Digital Sales Engine</span> backed by a
                <span className="text-red-500"> Personal Brand</span> that creates instant trust and turns
                strangers into paying clients.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TWO OFFERINGS */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <div className="eyebrow mb-4">What we do</div>
                <h2 className="font-serif-display text-4xl md:text-6xl max-w-2xl leading-[1.05]">
                  Two engines.<br />One <em className="gold-shine font-medium not-italic">unfair advantage</em>.
                </h2>
              </div>
              <Link to="/services" className="btn-ghost" data-testid="home-explore-services-btn">
                See both services <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>

          <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {offerings.map((o, i) => {
              const Icon = o.icon;
              return (
                <StaggerItem key={i}>
                  <Link to={o.link} className="block luxury-card p-8 md:p-10 h-full group hover:border-red-500/30 transition-colors" data-testid={`home-offering-${i}`}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-11 h-11 bg-red-500/10 border border-red-500/20 flex items-center justify-center" style={{ borderRadius: 0 }}>
                        <Icon size={18} className="text-red-500" />
                      </div>
                      <div className="eyebrow text-red-400">{o.tag}</div>
                    </div>
                    <h3 className="font-serif-display text-3xl md:text-4xl mb-4 leading-tight">{o.title}</h3>
                    <p className="text-zinc-400 font-body leading-relaxed mb-8">{o.desc}</p>
                    <div className="text-white text-sm tracking-wide inline-flex items-center gap-2 group-hover:text-red-400 transition-colors">
                      {o.cta} <ArrowUpRight size={16} />
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* 6-STEP SYSTEM TEASER */}
      <section className="relative py-24 md:py-32 border-t border-white/5 tl-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow mb-5">The Digital Sales Engine</div>
            <h2 className="font-serif-display text-4xl md:text-6xl max-w-3xl leading-[1.05]">
              6 stages. <em className="gold-shine font-medium not-italic">One predictable pipeline</em>.
            </h2>
            <p className="mt-6 text-zinc-400 font-body max-w-2xl">
              Every stage is measured, tracked and optimised weekly. If clicks are low, we fix the ad.
              If bookings are low, we fix the offer. If close rates dip, we fix the framework.
            </p>
          </Reveal>

          <StaggerGroup className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {engineSteps.map((p, i) => {
              const Icon = p.icon;
              return (
                <StaggerItem key={i}>
                  <div className="luxury-card p-7 h-full" data-testid={`home-step-${i}`}>
                    <div className="flex items-center justify-between mb-5">
                      <span className="font-serif-display text-3xl text-red-500">{p.n}</span>
                      <div className="w-10 h-10 bg-red-500/10 border border-red-500/20 flex items-center justify-center" style={{ borderRadius: 0 }}>
                        <Icon size={16} className="text-red-500" />
                      </div>
                    </div>
                    <div className="font-serif-display text-xl mb-2">{p.t}</div>
                    <p className="text-zinc-500 text-sm font-body leading-relaxed">{p.d}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <div className="eyebrow mb-4">Receipts, not promises</div>
                <h2 className="font-serif-display text-4xl md:text-6xl max-w-2xl leading-[1.05]">
                  Real numbers. <em className="gold-shine font-medium not-italic">Real leverage</em>.
                </h2>
              </div>
              <p className="md:max-w-sm text-zinc-400 font-body">
                Under the hood: Saurabh Khaspuri's 15-year track record in brand and direct-response growth.
              </p>
            </div>
          </Reveal>

          <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {stats.map((m, i) => (
              <StaggerItem key={i}>
                <div className="luxury-card p-8 md:p-10 h-full">
                  <div className="text-[11px] tracking-[0.22em] uppercase text-red-500/80 mb-4">{m.eyebrow}</div>
                  <div className="font-serif-display text-6xl md:text-7xl text-white leading-none">
                    <AnimatedCounter value={m.value} suffix={m.suffix} decimals={m.decimals} testId={`home-stat-${i}`} />
                  </div>
                  <div className="mt-4 text-zinc-500 text-sm font-body">{m.label}</div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <ClientMarquee testId="home-clients" className="tl-alt" />

      {/* FAQ */}
      <HomeFaq />

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/5 tl-red">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="luxury-card glow-ring relative overflow-hidden p-10 md:p-16">
              <div className="absolute -top-32 -right-32 w-80 h-80 bg-red-500/20 blur-3xl rounded-full pointer-events-none" />
              <div className="eyebrow mb-5">The next step</div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05]">
                Let's map your <br />
                <span className="gold-shine">acquisition engine</span>.
              </h2>
              <p className="mt-6 text-zinc-400 font-body max-w-xl">
                A 30-minute Growth Audit with Saurabh. We diagnose the leak in your current funnel —
                and show you exactly what a Digital Sales Engine would deliver for your business.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/book" className="btn-primary" data-testid="home-final-cta">
                  Book a Growth Audit <ArrowUpRight size={18} />
                </Link>
                <Link to="/services" className="btn-ghost" data-testid="home-final-services-btn">
                  See services
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
